import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  CreateProjectInput,
  ProjectBillingType,
  ProjectStatus,
  ProjectType,
} from '@dsr/shared-types';
import { useCreateProject, useProjects, type ProjectFilters } from '../api/queries/financials';
import { useOrganizations } from '../api/queries/clients';
import { toast } from '../components/Toasts';
import {
  Button,
  Dialog,
  FormField,
  Input,
  Select,
  StatusBadge,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';
import { fmtMoney } from '../utils/money';

export const PROJECT_TYPES: { value: ProjectType; label: string }[] = [
  { value: 'security_services', label: 'Security Services' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'investigation', label: 'Investigation' },
  { value: 'training', label: 'Training' },
  { value: 'event', label: 'Event' },
  { value: 'other', label: 'Other' },
];

export const PROJECT_STATUSES: { value: ProjectStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'closed', label: 'Closed' },
  { value: 'canceled', label: 'Canceled' },
];

const BILLING_TYPES: { value: ProjectBillingType; label: string }[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'flat_fee', label: 'Flat Fee' },
  { value: 'retainer', label: 'Retainer' },
];

export function projectTypeLabel(t: ProjectType): string {
  return PROJECT_TYPES.find((x) => x.value === t)?.label ?? t;
}

export function ProjectsPage(): JSX.Element {
  const [filters, setFilters] = useState<ProjectFilters>({});
  const [qDraft, setQDraft] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, isError, error } = useProjects(filters);
  const canWrite = can('project.write');
  const projects = data?.projects ?? [];

  // Standalone filter selects use the literal "all" sentinel (Radix Select
  // forbids an empty value); "all" maps back to no filter on the query.
  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...PROJECT_STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];
  const typeFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All types' },
    ...PROJECT_TYPES.map((t) => ({ value: t.value, label: t.label })),
  ];

  return (
    <section className="page projects-page">
      <header className="page-header">
        <div>
          <h1>Projects</h1>
          <p className="page-sub">Billable engagements — expenses and income roll up here.</p>
        </div>
        {canWrite && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New project
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <form
          className="filter-search"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters((f) => ({ ...f, q: qDraft }));
          }}
        >
          <input
            type="search"
            className="form-input"
            placeholder="Search title or number…"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
          />
        </form>

        <Select
          value={filters.status ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              status: v === 'all' ? undefined : (v as ProjectStatus),
            }))
          }
          options={statusFilterOptions}
          aria-label="Filter by status"
        />

        <Select
          value={filters.project_type ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              project_type: v === 'all' ? undefined : (v as ProjectType),
            }))
          }
          options={typeFilterOptions}
          aria-label="Filter by type"
        />
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {isError && (
        <p className="error-text" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load projects.'}
        </p>
      )}
      {!isLoading && !isError && projects.length === 0 && (
        <p className="muted">No projects match the current filters.</p>
      )}

      {projects.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Project</Table.HeaderCell>
              <Table.HeaderCell>Client</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Billing</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Target end</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {projects.map((p) => (
              <Table.Row key={p.id}>
                <Table.Cell tone="strong">
                  <Link to={`/projects/${p.id}`} className="table-link">{p.title}</Link>
                  <div className="muted small">{p.project_no}</div>
                </Table.Cell>
                <Table.Cell>{p.organization_name ?? '—'}</Table.Cell>
                <Table.Cell>{projectTypeLabel(p.project_type)}</Table.Cell>
                <Table.Cell>
                  {p.billing_type === 'flat_fee'
                    ? `Flat ${fmtMoney(p.flat_fee_amount)}`
                    : BILLING_TYPES.find((b) => b.value === p.billing_type)?.label}
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge status={p.status}>{p.status.replace('_', ' ')}</StatusBadge>
                </Table.Cell>
                <Table.Cell>{fmtDate(p.target_end_at)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && <CreateProjectModal onClose={() => setCreateOpen(false)} />}
    </section>
  );
}

function CreateProjectModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [orgId, setOrgId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState<ProjectType>('security_services');
  const [billing, setBilling] = useState<ProjectBillingType>('hourly');
  const [flatFee, setFlatFee] = useState('');
  const [notes, setNotes] = useState('');

  const { data: orgsData } = useOrganizations({});
  const create = useCreateProject();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (orgId === '') return;
    const input: CreateProjectInput = {
      organization_id: orgId,
      title: title.trim(),
      project_type: type,
      billing_type: billing,
    };
    if (billing === 'flat_fee') input.flat_fee_amount = flatFee;
    if (notes.trim()) input.notes = notes.trim();

    try {
      await create.mutateAsync(input);
      toast.success('Project created.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to create project.');
    }
  };

  return (
    <Dialog
      title="New project"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-project-form"
            variant="primary"
            disabled={
              create.isPending || title.trim().length === 0 || orgId === ''
              || (billing === 'flat_fee' && !/^\d+(\.\d{1,2})?$/.test(flatFee))
            }
          >
            {create.isPending ? 'Saving…' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="create-project-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Client" required>
          <select
            className="form-input"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value === '' ? '' : Number(e.target.value))}
            required
          >
            <option value="">Select a client…</option>
            {(orgsData?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </FormField>
        <FormField label="Type">
          <select className="form-input" value={type} onChange={(e) => setType(e.target.value as ProjectType)}>
            {PROJECT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Billing">
          <select
            className="form-input"
            value={billing}
            onChange={(e) => setBilling(e.target.value as ProjectBillingType)}
          >
            {BILLING_TYPES.map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
        </FormField>
        {billing === 'flat_fee' && (
          <FormField label="Flat fee amount" required>
            <Input
              value={flatFee}
              onChange={(e) => setFlatFee(e.target.value)}
              placeholder="2500.00"
              inputMode="decimal"
              required
            />
          </FormField>
        )}
        <label className="form-field form-field-full">
          <span>Notes</span>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </form>
    </Dialog>
  );
}
